// page-meeting-steps.ts — Déroulé « Préparez, animez, concluez » et pastilles
// numérotées de la fiche réunion.
//
// Extrait de page-meeting.ts (campagne strangler, août 2026). Rendu pur : le
// câblage des raccourcis (clic sur un numéro → onglet) reste dans la page, qui
// seule connaît `requestSwitchTab`.
//
// Nommé « steps » et non « guide » : le mot « guide » désigne depuis le
// renommage d'août 2026 les guides de réunion (bonnes pratiques déclarables,
// cf. page-meeting-guides.ts). Ici il ne s'agit que des 6 étapes du déroulé.
//
// Les 6 étapes correspondent 1:1 aux 6 premiers onglets. Le même numéro préfixe
// l'onglet et sert de raccourci dans le déroulé. Trois familles de bleu
// regroupent les phases — les mêmes que le récapitulatif des guides, via les
// variables `--phase-*` (cf. app.css).
//
// `esc` et `t` sont des globales (cf. types/core-globals.d.ts).

import type { TabKey } from '../domain/meeting-form.js';

export const STEP_TO_TAB: Record<number, TabKey> = {
  1: 'participants',
  2: 'actions',
  3: 'support',
  4: 'notes',
  5: 'cr',
  6: 'generation',
};

export const TAB_TO_STEP: Partial<Record<TabKey, number>> = {
  participants: 1,
  actions: 2,
  support: 3,
  notes: 4,
  cr: 5,
  generation: 6,
};

/** Fond et couleur de texte d'une pastille numérotée, par phase. */
function stepBadgeColors(n: number): { bg: string; fg: string } {
  if (n <= 3) return { bg: 'var(--phase-prepare)', fg: '#ffffff' };
  if (n === 4) return { bg: 'var(--phase-animate)', fg: '#ffffff' };
  return { bg: 'var(--phase-conclude)', fg: 'var(--phase-prepare)' };
}

/** Pastille ronde numérotée, colorée selon la phase. */
export function stepBadge(n: number, extraStyle = ''): string {
  const c = stepBadgeColors(n);
  return `<span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:${c.bg};color:${c.fg};font-size:11px;font-weight:700;line-height:1;flex:none;${extraStyle}">${n}</span>`;
}

function stepRow(n: number, tabLabel: (k: TabKey) => string): string {
  const label = tabLabel(STEP_TO_TAB[n] as TabKey);
  const tip = t('pageMeeting.steps.goToTab', { tab: label });
  return `<div style="display:flex;align-items:flex-start;gap:8px">
      <button type="button" class="mp-step" data-tab="${STEP_TO_TAB[n]}" title="${esc(tip)}" aria-label="${esc(tip)}" style="background:none;border:none;padding:0;margin-top:1px;cursor:pointer">${stepBadge(n)}</button>
      <span>${esc(t('pageMeeting.steps.step' + n))}</span>
    </div>`;
}

function stepsBubble(nums: number[], phase: string, tabLabel: (k: TabKey) => string): string {
  return `<div style="background:var(--phase-${phase}-bg);border-left:3px solid var(--phase-${phase});border-radius:8px;padding:8px 10px;display:flex;flex-direction:column;gap:8px">${nums
    .map((n) => stepRow(n, tabLabel))
    .join('')}</div>`;
}

/** Encart du déroulé : 2e colonne du bandeau d'informations. */
export function renderStepsBlock(tabLabel: (k: TabKey) => string): string {
  return `
    <div class="card" style="margin-top:0;background:var(--bg2);font-size:13px;line-height:1.55">
      <h2 style="font-size:14px;margin:0 0 10px 0;color:var(--text)">${esc(t('pageMeeting.steps.title'))}</h2>
      <div style="display:flex;flex-direction:column;gap:8px;color:var(--text2)">
        ${stepsBubble([1, 2, 3], 'prepare', tabLabel)}
        ${stepsBubble([4], 'animate', tabLabel)}
        ${stepsBubble([5, 6], 'conclude', tabLabel)}
      </div>
    </div>`;
}

// ── Écrans d'attente des onglets ─────────────────────────────────────────────
// Extraits de page-meeting.ts (cliquet fonction, août 2026). Rendu pur.

/** Onglet ouvert avant le premier enregistrement : rien à afficher encore. */
export function renderSaveFirstPlaceholder(): string {
  return `
      <div class="card" style="text-align:center;padding:32px 24px">
        <h2 style="font-size:15px;margin:0 0 8px 0;color:var(--text)">${esc(t('pageMeeting.tab.saveFirstTitle'))}</h2>
        <p style="margin:0;color:var(--text2);font-size:13px;line-height:1.5">${esc(t('pageMeeting.tab.saveFirstHint'))}</p>
      </div>`;
}

/** Réunion brouillon issue du calendrier : sans comité, les onglets sont
 *  verrouillés (H5) jusqu'au rattachement via le bloc Informations. */
export function renderNoCommitteePlaceholder(): string {
  return `
      <div class="card" style="text-align:center;padding:32px 24px">
        <h2 style="font-size:15px;margin:0 0 8px 0;color:var(--text)">${esc(t('pageMeeting.tab.noCommitteeTitle'))}</h2>
        <p style="margin:0;color:var(--text2);font-size:13px;line-height:1.5">${esc(t('pageMeeting.tab.noCommitteeHint'))}</p>
      </div>`;
}

/**
 * Câble les numéros du déroulé : un clic bascule sur l'onglet correspondant et
 * ramène la barre d'onglets dans le champ de vision.
 *
 * Les étapes pointant un onglet masqué (rôle, réunion non enregistrée) sont
 * inertes plutôt que retirées : le déroulé « Préparez, animez, concluez » reste
 * lisible en entier, même quand tout n'est pas encore accessible.
 */
export function wireSteps(
  visibleTabs: readonly TabKey[],
  switchTo: (key: TabKey, after: () => void) => void,
): void {
  document.querySelectorAll<HTMLButtonElement>('.mp-step').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset['tab'] as TabKey | undefined;
      if (!key || !visibleTabs.includes(key)) return;
      switchTo(key, () => {
        document.getElementById('mp-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  });
}
